import { API_URL } from '../utils/helpers';
import { WithdrawalReceipt, WithdrawalSession } from '../utils/types';
import {
  lookupHeaders,
  OrderLookupParams,
  isWithdrawalDisabled,
  shopIdentityOf,
  VerifiedShop,
} from './orderLookupService';

// Dedicated login: the same storefront proof and shop as the manual return.
export const postWithdrawalSession = (
  params: OrderLookupParams,
  proof: string | null,
  shop: string | null
): Promise<Response> =>
  fetch(API_URL + '/withdrawals/session', {
    method: 'POST',
    headers: lookupHeaders(proof, shop),
    body: JSON.stringify(params),
  });

// #166: the full-screen QR (no storefront proof, no shop) withdraws with the
// parcel QR itself; return-api verifies it (qr-exchange) and the shop comes
// from that verification only. The QR is reusable (stateless credential), so
// the return redemption of the same scan does not spend it. Never stored.
export const postWithdrawalSessionByQrStandalone = (
  qr: string
): Promise<Response> =>
  fetch(API_URL + '/withdrawals/session/by-qr', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ qr }),
  });

// The shop of a proof-less QR withdrawal answer, accepted only when it is the
// shop of the order and the answer carries a session token.
export const verifiedWithdrawalShopOf = (
  body: unknown
): VerifiedShop | null => {
  const answer = body as
    | (Partial<WithdrawalSession> & { shop?: unknown })
    | null;
  if (!answer || typeof answer.token !== 'string' || answer.token === '') {
    return null;
  }
  const shop = shopIdentityOf(answer.shop);
  return shop && shop.id === answer.order?.shop_id ? shop : null;
};

export const withdrawalEntryVisible = async (
  shop: string | null,
  signal: AbortSignal
): Promise<boolean> => {
  // A proof-less entry has no embed shop. Never infer one from storage or rq.
  if (!shop) return true;
  try {
    const response = await fetch(
      API_URL +
        '/settings/portal/withdrawal?shop=' +
        encodeURIComponent(shop.trim().toLowerCase()),
      { signal }
    );
    if (!response.ok) return true;
    const body = await response.json();
    return body?.entry_visible !== false;
  } catch {
    return true;
  }
};

export type { WithdrawalReceipt, WithdrawalSession };

export interface WithdrawalDeclaration {
  order_id: number;
  name: string;
  email: string;
  locale: string;
}

export const postWithdrawal = (
  body: WithdrawalDeclaration,
  token: string,
  shopId: number,
  idempotencyKey: string
): Promise<Response> =>
  fetch(API_URL + '/withdrawals', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
      'Shop-Id': String(shopId),
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify(body),
  });

export const getWithdrawal = (
  id: number,
  token: string,
  shopId: number
): Promise<Response> =>
  fetch(API_URL + '/withdrawals/' + id, {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + token,
      'Shop-Id': String(shopId),
    },
  });

const codeOf = (body: unknown): unknown =>
  body && typeof body === 'object'
    ? (body as { code?: unknown }).code
    : undefined;

export type DeclareError =
  | 'invalid'
  | 'sessionExpired'
  | 'notFound'
  | 'conflict'
  | 'disabledByShop'
  | 'portalInactive'
  | 'generic';

// the failure of a declaration / receipt read, by its status and code
export const declareErrorOf = (status: number, body: unknown): DeclareError => {
  switch (status) {
    case 400:
      return codeOf(body) === 'WITHDRAWAL_INVALID' ? 'invalid' : 'generic';
    case 403:
      return 'sessionExpired';
    case 404:
      return 'notFound';
    case 409:
      return 'conflict';
    case 410:
      return isWithdrawalDisabled(status, body)
        ? 'disabledByShop'
        : 'portalInactive';
    default:
      return 'generic';
  }
};

// return-api accepts 1-64 of [A-Za-z0-9._:-] (case-sensitive)
export const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9._:-]{1,64}$/;

// one key per declaration attempt sequence (persisted by the form, reused on
// every retry)
export const newIdempotencyKey = (): string => {
  const c = (globalThis as { crypto?: Partial<Crypto> }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  return (
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2) +
    Math.random().toString(36).slice(2)
  ).slice(0, 64);
};
